import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography } from "@mui/material";
import { legalContent } from "../content/legal";
import { linkifyPaths } from "./linkifyPaths";

/** Renders a structured legal document (privacy / terms / sub-processors) in the
 *  active language, falling back to Dutch. Content lives in src/content/legal.ts. */
export default function LegalPage({ docKey }: { docKey: "privacy" | "terms" | "subprocessors" | "privacystandaard" }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "nl").slice(0, 2);
  const doc = legalContent[docKey]?.[lang] || legalContent[docKey]?.nl;
  if (!doc) return null;
  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 } }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "2.25rem", sm: "2.75rem" }, mb: 1 }}>{doc.title}</Typography>
        <Typography sx={{ mb: 3, color: "text.secondary", fontSize: ".9rem" }}>{t("legal.lastUpdated")}</Typography>
        <Typography sx={{ mb: 4, color: "text.secondary", lineHeight: 1.7 }}>{linkifyPaths(doc.intro)}</Typography>
        {doc.sections.map((s, i) => (
          <Box key={i} sx={{ mb: 3.5 }}>
            <Typography variant="h2" sx={{ fontSize: "1.3rem", mb: 1.25 }}>{s.heading}</Typography>
            {s.paragraphs.map((p, j) => (
              <Typography key={j} sx={{ mb: 1.5, color: "text.secondary", lineHeight: 1.7 }}>{linkifyPaths(p)}</Typography>
            ))}
            {s.bullets && s.bullets.length > 0 && (
              <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 3, color: "text.secondary", lineHeight: 1.7 }}>
                {s.bullets.map((b, k) => <li key={k}>{linkifyPaths(b)}</li>)}
              </Box>
            )}
          </Box>
        ))}
      </Container>
    </Box>
  );
}
